'use client';

import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';

import { StepName } from './useBookingSteps';

type BookingType = 'SINGLE' | 'GROUP' | 'RANGE';
type PaymentMethod = 'cash' | 'online';

type LocationKind = 'venue' | 'at_home' | 'online';

const isBookingType = (value: string | null): value is BookingType =>
  value === 'SINGLE' || value === 'GROUP' || value === 'RANGE';
const isPaymentMethod = (value: string | null): value is PaymentMethod => value === 'cash' || value === 'online';
const isLocationKind = (value: string | null): value is LocationKind =>
  value === 'venue' || value === 'at_home' || value === 'online';

/**
 * Hook for managing booking flow state in URL search params.
 * Uses Next.js navigation APIs for URL state management.
 *
 * URL params:
 * - step: Current step in the booking flow
 * - slotId: Selected slot ID
 * - date: Booking date (YYYY-MM-DD)
 * - startTime: Time slot start (minutes from midnight)
 * - endTime: Time slot end (minutes from midnight)
 * - locationId: Chosen delivery location ID - disambiguates a both-mode slot's
 *   two same-window rows so a refresh/deeplink highlights only the picked one
 * - locationKind: 'venue' | 'at_home' | 'online'; keeps the online flow (no address
 *   step, "En visio" summary) intact across a refresh
 * - addressId: Selected saved address ID (optional)
 * - bookingType: 'SINGLE' or 'GROUP'
 * - numberOfPeople: Number of people for group bookings
 * - paymentMethod: 'cash' or 'online'
 */
export const useBookingUrlState = () => {
  const searchParams = useSearchParams();

  const getStringParam = useCallback(
    (key: string) => {
      const value = searchParams.get(key);
      return value ?? null;
    },
    [searchParams]
  );

  const getIntegerParam = useCallback(
    (key: string) => {
      const raw = searchParams.get(key);
      if (raw === null) return null;
      const parsed = Number.parseInt(raw, 10);
      return Number.isNaN(parsed) ? null : parsed;
    },
    [searchParams]
  );

  const setQueryParams = useCallback((updates: Record<string, string | number | null>) => {
    // Booking-flow params are CLIENT state for deep-link / refresh only. Sync the
    // URL with the native History API instead of `router.replace`: in the App
    // Router, router.replace issues an RSC navigation that, on this no-store
    // gated route, degrades into a FULL PAGE RELOAD on every param change (picking
    // a date reloaded the whole wizard). `window.history.replaceState` updates the
    // address bar with no fetch and no navigation, and Next mirrors
    // useSearchParams / usePathname from it (supported since 14.1) so reads stay
    // live. Read the LIVE URL (window.location) so merged updates always build on
    // the latest state.
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null) {
        params.delete(key);
      } else {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    const url = query ? `${window.location.pathname}?${query}` : window.location.pathname;
    // Pass a FRESH state object ({}), NOT window.history.state: Next patches
    // replaceState and EARLY-RETURNS (skipping the useSearchParams sync) when the
    // passed state carries its internal `__NA`/tree markers — which is exactly
    // what `window.history.state` holds after hydration. With {} Next re-copies
    // its internals in AND runs the sync, so getters (urlDate, urlLocationId, …)
    // stay live; it still does a client-only restore (no RSC fetch, no reload).
    window.history.replaceState({}, '', url);
  }, []);

  const setQueryParam = useCallback(
    (key: string, value: string | number | null) => setQueryParams({ [key]: value }),
    [setQueryParams]
  );

  const step = getStringParam('step');
  const slotId = getStringParam('slotId');
  const date = getStringParam('date');
  const startTime = getIntegerParam('startTime');
  const endTime = getIntegerParam('endTime');
  const locationId = getStringParam('locationId');
  const rawLocationKind = getStringParam('locationKind');
  const locationKind = isLocationKind(rawLocationKind) ? rawLocationKind : null;
  const addressId = getStringParam('addressId');

  const rawBookingType = getStringParam('bookingType');
  const bookingType = isBookingType(rawBookingType) ? rawBookingType : null;

  const numberOfPeople = getIntegerParam('numberOfPeople');

  const rawPaymentMethod = getStringParam('paymentMethod');
  const paymentMethod = isPaymentMethod(rawPaymentMethod) ? rawPaymentMethod : null;

  const setStepTyped = useCallback((value: StepName | null) => setQueryParam('step', value), [setQueryParam]);
  const setSlotId = useCallback((value: string | null) => setQueryParam('slotId', value), [setQueryParam]);
  const setDate = useCallback((value: string | null) => setQueryParam('date', value), [setQueryParam]);
  const setStartTime = useCallback((value: number | null) => setQueryParam('startTime', value), [setQueryParam]);
  const setEndTime = useCallback((value: number | null) => setQueryParam('endTime', value), [setQueryParam]);
  const setLocationId = useCallback((value: string | null) => setQueryParam('locationId', value), [setQueryParam]);
  const setLocationKind = useCallback(
    (value: LocationKind | null) => setQueryParam('locationKind', value),
    [setQueryParam]
  );
  const setAddressId = useCallback((value: string | null) => setQueryParam('addressId', value), [setQueryParam]);
  const setBookingType = useCallback(
    (value: BookingType | null) => setQueryParam('bookingType', value),
    [setQueryParam]
  );
  const setNumberOfPeople = useCallback(
    (value: number | null) => setQueryParam('numberOfPeople', value),
    [setQueryParam]
  );
  const setPaymentMethod = useCallback(
    (value: PaymentMethod | null) => setQueryParam('paymentMethod', value),
    [setQueryParam]
  );

  const setTimeSlot = useCallback(
    (start: number | null, end: number | null) => {
      setQueryParams({
        startTime: start,
        endTime: end
      });
    },
    [setQueryParams]
  );

  const clearAll = useCallback(() => {
    setQueryParams({
      step: null,
      slotId: null,
      date: null,
      startTime: null,
      endTime: null,
      locationId: null,
      locationKind: null,
      addressId: null,
      bookingType: null,
      numberOfPeople: null,
      paymentMethod: null
    });
  }, [setQueryParams]);

  return useMemo(
    () => ({
      // Values (null if not set)
      step: step as StepName | null,
      slotId,
      date,
      startTime,
      endTime,
      locationId,
      locationKind,
      addressId,
      bookingType,
      numberOfPeople,
      paymentMethod,

      // Setters
      setStep: setStepTyped,
      setSlotId,
      setDate,
      setStartTime,
      setEndTime,
      setLocationId,
      setLocationKind,
      setAddressId,
      setBookingType,
      setNumberOfPeople,
      setPaymentMethod,

      // Convenience method to set time slot at once
      setTimeSlot,

      // Clear all booking params
      clearAll
    }),
    [
      step,
      slotId,
      date,
      startTime,
      endTime,
      locationId,
      locationKind,
      addressId,
      bookingType,
      numberOfPeople,
      paymentMethod,
      setStepTyped,
      setSlotId,
      setDate,
      setStartTime,
      setEndTime,
      setLocationId,
      setLocationKind,
      setAddressId,
      setBookingType,
      setNumberOfPeople,
      setPaymentMethod,
      setTimeSlot,
      clearAll
    ]
  );
};

// Re-export for backward compatibility during transition
export const useBookingUrlSync = useBookingUrlState;
